/**
 * Vue Router Configuration
 *
 * Aggregates routes from all modules and applies global guards
 */
import type { RouteRecordRaw } from 'vue-router';
import i18next from 'i18next';
import { authRoutes } from '@/modules/auth';
import { dashboardRoutes } from '@/modules/dashboard';
import { AppLayout } from '@/shared/components';
import { patientsRoutes } from '@/modules/patients';
import { mothersRoutes } from '@/modules/mothers';
import { donorsRoutes } from '@/modules/donors';
import { milkRoutes } from '@/modules/milk';
import { inventoryRoutes } from '@/modules/customer-inventory';
import { usersRoutes } from '@/modules/users';
import { profileRoutes } from '@/modules/profile';
import { feedProcessingRoutes } from '@/modules/feed-processing';
import { feedAdministrationRoutes } from '@/modules/feed-administration';
import { settingsRoutes, settingsWelcomeRoute, settingsConfigureRoute } from '@/modules/settings';
import { recallRoutes } from '@/modules/recall';
import { reportsRoutes } from '@/modules/reports';
import { auditLogsRoutes } from '@/modules/audit-logs';
import { disposeAreaRoutes } from '@/modules/dispose-area';
import { moveInventoryRoutes } from '@/modules/move-inventory';
import { interoperabilityRoutes } from '@/modules/interoperability';

// Aggregate all module routes
export const routes: RouteRecordRaw[] = [
  // Routes with layout (authenticated pages)
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      ...dashboardRoutes,
      ...patientsRoutes,
      ...mothersRoutes,
      ...donorsRoutes,
      ...milkRoutes,
      ...inventoryRoutes,
      ...moveInventoryRoutes,
      ...usersRoutes,
      ...profileRoutes,
      ...feedProcessingRoutes,
      ...feedAdministrationRoutes,
      ...recallRoutes,
      ...reportsRoutes,
      ...auditLogsRoutes,
      ...disposeAreaRoutes,
      ...settingsRoutes,
      ...interoperabilityRoutes,
    ],
  },
  // Routes without layout (public pages)
  ...authRoutes,
  // Standalone welcome/checklist page — no sidebar/header/banner (see
  // settingsWelcomeRoute's doc comment in modules/settings/router.ts).
  settingsWelcomeRoute,
  // Standalone configuration stepper page — same chrome-free treatment as
  // settingsWelcomeRoute (see settingsConfigureRoute's doc comment in
  // modules/settings/router.ts).
  settingsConfigureRoute,
  // Health check page (no layout chrome needed)
  {
    path: '/healthcheck',
    name: 'health-check',
    component: () => import('../views/HealthCheckView.vue'),
    meta: {
      title: () => i18next.t('router.apiHealthCheck'),
      requiresAuth: false,
      // Bypass the settings-onboarding wizard guard — this page must always be
      // reachable regardless of first-time setup state.
      skipSettingsGuard: true,
    },
  },
  // 404 catch-all — also exempt so unknown/external paths are never redirected
  // to the settings wizard (e.g. /swagger, /api-docs served by the API container).
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { requiresAuth: false, skipSettingsGuard: true },
  },
];
